import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Bell, AlertOctagon, AlertTriangle, Info, RefreshCw,
  ChevronDown, ChevronRight, Wand2, Activity, Database, Clock,
  CheckCircle2, FileBarChart2
} from 'lucide-react';
import { fmtNumber } from '../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERITY = {
  critical: {
    label: 'Critical',
    icon: AlertOctagon,
    color: 'text-rose-400',
    bg: 'bg-rose-500/10',
    border: 'border-rose-500/30',
    badge: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
    dot: 'bg-rose-500',
  },
  warning: {
    label: 'Warning',
    icon: AlertTriangle,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    badge: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    dot: 'bg-amber-500',
  },
  info: {
    label: 'Info',
    icon: Info,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    badge: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    dot: 'bg-blue-500',
  },
};

const CATEGORY_ICON = {
  aggregation: FileBarChart2,
  freshness: Clock,
  data: Database,
  computation: Wand2,
  default: Activity,
};

export default function NotificationCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [computing, setComputing] = useState({});
  const [filter, setFilter] = useState('all'); // all|critical|warning|info

  const loadLatest = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/data-quality/latest`, { credentials: 'include' });
      const j = await res.json();
      if (j.success) setData(j);
      else setData({ summary: null, reports: [] });
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await fetch(`${API_URL}/api/data-quality/scan`, { credentials: 'include' });
      const j = await res.json();
      if (j.success) setData(j);
    } catch (e) { console.error(e); }
    setScanning(false);
  };

  const computeRFM = async (brandId) => {
    setComputing(c => ({ ...c, [brandId]: true }));
    try {
      const res = await fetch(`${API_URL}/api/data-quality/compute-rfm/${brandId}`, {
        method: 'POST', credentials: 'include',
      });
      const j = await res.json();
      if (j.success) {
        // Re-scan that brand
        const rescanRes = await fetch(`${API_URL}/api/data-quality/scan/${brandId}`, { credentials: 'include' });
        const rescan = await rescanRes.json();
        if (rescan.success && data) {
          // Update single brand in-place
          setData(prev => ({
            ...prev,
            reports: prev.reports.map(r => r.brand_id === brandId ? rescan.report : r),
          }));
        }
      } else {
        alert(j.message || 'RFM compute failed');
      }
    } catch (e) {
      console.error(e);
      alert('RFM compute failed');
    }
    setComputing(c => ({ ...c, [brandId]: false }));
  };

  useEffect(() => { loadLatest(); }, []);

  if (loading) {
    return <div className="p-6 text-muted-foreground" data-testid="notification-center-loading">Loading data quality scan…</div>;
  }

  const reports = data?.reports || [];
  const summary = data?.summary || { critical: 0, warning: 0, info: 0, brands_scanned: 0 };

  // Sort: brands with most critical first
  const sortedReports = [...reports].sort((a, b) => {
    const ac = a.severity_counts?.critical || 0;
    const bc = b.severity_counts?.critical || 0;
    if (ac !== bc) return bc - ac;
    const aw = a.severity_counts?.warning || 0;
    const bw = b.severity_counts?.warning || 0;
    return bw - aw;
  });

  return (
    <div className="p-6 space-y-6 max-w-7xl" data-testid="notification-center-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Bell className="w-8 h-8 text-primary" />
            Data Quality Notifications
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-3xl">
            Per-brand integrity scan. Surfaces missing data, stale syncs, and computation gaps that would cause inaccurate dashboards. Fix issues before production migration.
            {data?.scanned_at && (
              <span className="ml-2 text-xs">· Last scan: {new Date(data.scanned_at).toLocaleString('en-IN')}</span>
            )}
          </p>
        </div>
        <Button onClick={runScan} disabled={scanning} data-testid="run-scan-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${scanning ? 'animate-spin' : ''}`} />
          {scanning ? 'Scanning all brands…' : 'Re-scan all brands'}
        </Button>
      </div>

      {/* Summary cards (clickable filter) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SumCard
          label="Brands Scanned"
          value={fmtNumber(summary.brands_scanned)}
          icon={Activity}
          accent="text-blue-400"
        />
        <SumCard
          label="Critical"
          value={fmtNumber(summary.critical)}
          icon={AlertOctagon}
          accent="text-rose-400"
          onClick={() => setFilter(filter === 'critical' ? 'all' : 'critical')}
          active={filter === 'critical'}
        />
        <SumCard
          label="Warnings"
          value={fmtNumber(summary.warning)}
          icon={AlertTriangle}
          accent="text-amber-400"
          onClick={() => setFilter(filter === 'warning' ? 'all' : 'warning')}
          active={filter === 'warning'}
        />
        <SumCard
          label="Info"
          value={fmtNumber(summary.info)}
          icon={Info}
          accent="text-blue-400"
          onClick={() => setFilter(filter === 'info' ? 'all' : 'info')}
          active={filter === 'info'}
        />
      </div>

      {/* Empty state */}
      {sortedReports.length === 0 && (
        <Card className="border-border bg-card/50 text-center py-12" data-testid="notification-empty">
          <CardContent>
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400 mb-3" />
            <div className="text-lg font-semibold">No scan available</div>
            <div className="text-sm text-muted-foreground mt-1">Click "Re-scan all brands" to run the data quality check.</div>
          </CardContent>
        </Card>
      )}

      {/* Per-brand reports */}
      <div className="space-y-3">
        {sortedReports.map((report) => {
          const c = report.severity_counts?.critical || 0;
          const w = report.severity_counts?.warning || 0;
          const i = report.severity_counts?.info || 0;
          const total = c + w + i;
          const isExpanded = !!expanded[report.brand_id];

          // Filter findings by selected severity
          const visibleFindings = filter === 'all'
            ? (report.findings || [])
            : (report.findings || []).filter(f => f.severity === filter);

          // Hide entirely if filter is set and brand has no matching findings
          if (filter !== 'all' && visibleFindings.length === 0) return null;

          return (
            <Card
              key={report.brand_id}
              className={`border-border bg-card/40 backdrop-blur ${c > 0 ? 'border-rose-500/30' : w > 0 ? 'border-amber-500/30' : ''}`}
              data-testid={`brand-report-${report.brand_id}`}
            >
              <button
                onClick={() => setExpanded(e => ({ ...e, [report.brand_id]: !isExpanded }))}
                className="w-full p-4 text-left flex items-center gap-3 hover:bg-muted/20 transition"
                data-testid={`brand-report-toggle-${report.brand_id}`}
              >
                {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}

                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-2">
                    {report.brand_name}
                    <span className="text-xs text-muted-foreground font-mono">#{report.brand_id}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {fmtNumber(report.stats?.customers)} customers · {fmtNumber(report.stats?.transactions)} txns · {fmtNumber(report.stats?.stores)} stores
                  </div>
                </div>

                {total === 0 ? (
                  <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30">
                    <CheckCircle2 className="w-3 h-3 mr-1" />Clean
                  </Badge>
                ) : (
                  <div className="flex items-center gap-1.5">
                    {c > 0 && <Badge className={SEVERITY.critical.badge}>{c} Critical</Badge>}
                    {w > 0 && <Badge className={SEVERITY.warning.badge}>{w} Warning</Badge>}
                    {i > 0 && <Badge className={SEVERITY.info.badge}>{i} Info</Badge>}
                  </div>
                )}
              </button>

              {isExpanded && (
                <CardContent className="pt-0 pb-4 space-y-2 border-t border-border/50">
                  {report.error && (
                    <div className="p-3 mt-3 rounded bg-rose-500/10 border border-rose-500/30 text-sm">
                      Scan error: {report.error}
                    </div>
                  )}
                  {visibleFindings.length === 0 ? (
                    <div className="p-3 mt-3 text-center text-sm text-muted-foreground">
                      <CheckCircle2 className="w-5 h-5 inline-block text-emerald-400 mr-1" />
                      No findings for this brand at the selected severity.
                    </div>
                  ) : visibleFindings.map((f, idx) => {
                    const sev = SEVERITY[f.severity] || SEVERITY.info;
                    const SevIcon = sev.icon;
                    const CatIcon = CATEGORY_ICON[f.category] || CATEGORY_ICON.default;
                    const isRFM = f.title?.includes('RFM');
                    return (
                      <div
                        key={idx}
                        className={`mt-3 p-3 rounded border ${sev.border} ${sev.bg} flex items-start gap-3`}
                        data-testid={`finding-${report.brand_id}-${idx}`}
                      >
                        <SevIcon className={`w-5 h-5 ${sev.color} flex-shrink-0 mt-0.5`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm">{f.title}</span>
                            <span className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-border/50 text-muted-foreground">
                              <CatIcon className="w-2.5 h-2.5" />{f.category}
                            </span>
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">{f.detail}</div>
                          {f.fix_action && (
                            <div className="mt-2 text-xs flex items-center gap-2 flex-wrap">
                              <span className="text-muted-foreground">Fix:</span>
                              <code className="font-mono text-xs bg-muted/40 px-2 py-0.5 rounded border border-border/40">{f.fix_action}</code>
                              {isRFM && (
                                <Button
                                  size="sm"
                                  variant="default"
                                  className="h-7 text-xs"
                                  onClick={() => computeRFM(report.brand_id)}
                                  disabled={!!computing[report.brand_id]}
                                  data-testid={`compute-rfm-${report.brand_id}-btn`}
                                >
                                  <Wand2 className={`w-3 h-3 mr-1 ${computing[report.brand_id] ? 'animate-spin' : ''}`} />
                                  {computing[report.brand_id] ? 'Computing…' : 'Compute Now'}
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

const SumCard = ({ label, value, icon: Icon, accent, onClick, active }) => (
  <Card
    className={`border-border bg-card/50 backdrop-blur transition ${onClick ? 'cursor-pointer' : ''} ${active ? 'border-primary ring-1 ring-primary' : 'hover:border-primary/30'}`}
    onClick={onClick}
    data-testid={`notif-summary-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <CardContent className="p-4 flex items-center justify-between">
      <div>
        <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
        <div className="text-2xl font-bold mt-1">{value}</div>
      </div>
      <Icon className={`w-7 h-7 ${accent} opacity-70`} />
    </CardContent>
  </Card>
);
